import { Paper, Typography, TextField, Button, IconButton, Box, Alert } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import type { PRExperience } from '../../../types/salesforce'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export interface ExperiencesSectionProps {
  prSheetId?: string
  experiences: PRExperience[]
  onChange: (experiences: PRExperience[]) => void
  onBlur?: () => void
}

function SortableExperienceItem({
  experience,
  index,
  onDelete,
  onChange,
  onBlur,
}: {
  experience: PRExperience
  index: number
  onDelete: () => void
  onChange: (field: keyof PRExperience, value: string | number) => void
  onBlur?: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: experience.Id! })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Box ref={setNodeRef} style={style} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
            <DragIndicatorIcon />
          </IconButton>
          <Typography variant="subtitle2">取り組み #{index + 1}</Typography>
        </Box>
        <IconButton onClick={onDelete} size="small" color="error">
          <DeleteIcon />
        </IconButton>
      </Box>

      <TextField
        label="年度"
        fullWidth
        margin="normal"
        type="number"
        value={experience.Year__c || ''}
        onChange={(e) => onChange('Year__c', parseInt(e.target.value) || 0)}
        onBlur={onBlur}
      />

      <TextField
        label="タイトル"
        fullWidth
        margin="normal"
        value={experience.Title__c || ''}
        onChange={(e) => onChange('Title__c', e.target.value)}
        onBlur={onBlur}
      />

      <TextField
        label="概要"
        fullWidth
        margin="normal"
        multiline
        rows={3}
        value={experience.Overview__c || ''}
        onChange={(e) => onChange('Overview__c', e.target.value)}
        onBlur={onBlur}
      />

      <TextField
        label="成果"
        fullWidth
        margin="normal"
        multiline
        rows={3}
        value={experience.Results__c || ''}
        onChange={(e) => onChange('Results__c', e.target.value)}
        onBlur={onBlur}
      />
    </Box>
  )
}

export function ExperiencesSection({ prSheetId, experiences, onChange, onBlur }: ExperiencesSectionProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const handleAdd = () => {
    const newExperience: PRExperience = {
      Id: `temp-${Date.now()}`,
      PRSheet__c: prSheetId,
      Year__c: new Date().getFullYear(),
      Title__c: '',
      Overview__c: '',
      Results__c: '',
      SortOrder__c: (experiences.length + 1) * 10,
    }
    onChange([...experiences, newExperience])
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = experiences.findIndex((exp) => exp.Id === active.id)
      const newIndex = experiences.findIndex((exp) => exp.Id === over.id)

      const reorderedExperiences = arrayMove(experiences, oldIndex, newIndex)

      // SortOrder__cを更新
      const updatedExperiences = reorderedExperiences.map((exp, index) => ({
        ...exp,
        SortOrder__c: (index + 1) * 10,
      }))

      onChange(updatedExperiences)
      if (onBlur) onBlur()
    }
  }

  const handleChange = (index: number, field: keyof PRExperience, value: string | number) => {
    const newExperiences = [...experiences]
    newExperiences[index] = {
      ...newExperiences[index],
      [field]: value,
    }
    onChange(newExperiences)
  }

  const handleDelete = (index: number) => {
    const newExperiences = experiences.filter((_, i) => i !== index)
    onChange(newExperiences)
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">📌 これまでの主な取り組み</Typography>
        <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" size="small">
          追加
        </Button>
      </Box>

      <Alert severity="info" icon={<span>💡</span>} sx={{ mb: 2 }}>
        これまでに力を入れて取り組んできたことを書き出しましょう。メンバーとしてキャンプやスクールでの取り組んだこと、メンターとしての活動、学業、アルバイト、サークル、インターンなどが含まれます。
      </Alert>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={experiences.map((exp) => exp.Id!)} strategy={verticalListSortingStrategy}>
          {experiences.map((experience, index) => (
            <SortableExperienceItem
              key={experience.Id}
              experience={experience}
              index={index}
              onDelete={() => handleDelete(index)}
              onChange={(field, value) => handleChange(index, field, value)}
              onBlur={onBlur}
            />
          ))}
        </SortableContext>
      </DndContext>

      {experiences.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          取り組みを追加してください
        </Typography>
      )}
    </Paper>
  )
}
